import { FoodUnit } from "../data/foodUnit";
import { PurchaseUnit } from "../data/purchase";
import { NumberInput } from "./ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/Select";
import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ClassValue } from "clsx";
import { cn } from "../lib/utils";

/**
 * Gemeinsame strukturelle Basis von FoodUnit und PurchaseUnit – `UnitAmountBase`
 * braucht nur Id + Beschreibung (+ Nettogewicht für die Anzeige-Logik).
 */
export type AmountUnit = {
  id: number;
  description: string;
  netWeight?: number;
  isDefault?: boolean;
};

function UnitAmountBase<U extends AmountUnit>({
  unit,
  units,
  amount,
  onUnitChange,
  onAmountChange,
  className,
  dataCy,
}: {
  unit: U | undefined;
  units: U[];
  amount: number | undefined;
  onUnitChange: (u: U | undefined) => void;
  onAmountChange: (a: number) => void;
  className?: ClassValue;
  dataCy?: string;
}) {
  const [isFocused, setIsFocused] = useState(false);
  const [viewportStyle, setViewportStyle] = useState<React.CSSProperties>({
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
  });

  useEffect(() => {
    if (units.length === 1 && !unit) {
      onUnitChange(units[0]);
    }
  }, [units, unit, onUnitChange]);

  useEffect(() => {
    if (!isFocused || !window.visualViewport) {
      setViewportStyle({
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
      });
      return;
    }

    let rAFId: number;

    const handleResizeOrScroll = () => {
      const vv = window.visualViewport;
      if (!vv) return;

      rAFId = requestAnimationFrame(() => {
        setViewportStyle({
          position: "fixed",
          top: `${vv.offsetTop + vv.height - 44}px`,
          left: `${vv.offsetLeft}px`,
          width: `${vv.width}px`,
          transform: "none",
          right: "auto",
          bottom: "auto",
        });
      });
    };

    window.visualViewport.addEventListener("resize", handleResizeOrScroll);
    window.visualViewport.addEventListener("scroll", handleResizeOrScroll);

    handleResizeOrScroll();

    const timer = setTimeout(handleResizeOrScroll, 100);

    return () => {
      window.visualViewport?.removeEventListener(
        "resize",
        handleResizeOrScroll,
      );
      window.visualViewport?.removeEventListener(
        "scroll",
        handleResizeOrScroll,
      );
      cancelAnimationFrame(rAFId);
      clearTimeout(timer);
    };
  }, [isFocused]);

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <NumberInput
        data-cy={dataCy}
        value={amount}
        onValueChange={(v) => onAmountChange(v ?? 0)}
        className="h-8 w-24"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="none"
        spellCheck="false"
      />
      {units.length > 1 ? (
        <Select
          value={unit ? String(unit.id) : undefined}
          onValueChange={(v) =>
            onUnitChange(units.find((u) => String(u.id) === v))
          }
        >
          <SelectTrigger className="h-8 w-auto min-w-[5rem]">
            <SelectValue placeholder="Einheit" />
          </SelectTrigger>
          <SelectContent>
            {units.map((u) => (
              <SelectItem key={u.id} value={String(u.id)}>
                {u.description}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <span className="text-sm text-ink-muted min-w-[5rem]">
          {unit?.description}
        </span>
      )}

      {isFocused &&
        units.length > 1 &&
        localStorage.getItem("showUnitHelper") === "true" &&
        createPortal(
          <div
            className="fixed z-[9999] bg-surface-card border-t border-line p-1.5 flex flex-row gap-2 overflow-x-auto whitespace-nowrap shadow-sheet items-center"
            style={{
              ...viewportStyle,
              height: "44px",
              boxSizing: "border-box",
            }}
          >
            {units.map((u) => {
              const isActive = unit?.id === u.id;
              return (
                <button
                  key={u.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault(); // Prevents losing focus on the input!
                    onUnitChange(u);
                  }}
                  className={cn(
                    "px-4 h-7 rounded-full text-xs font-semibold border transition-all shrink-0 flex items-center justify-center",
                    isActive
                      ? "border-teal bg-teal/10 text-teal"
                      : "border-line bg-surface-card text-ink-muted hover:border-ink-muted hover:text-ink",
                  )}
                >
                  {u.description}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

export const UnitAmount = (props: {
  foodUnit: FoodUnit | undefined;
  units: FoodUnit[];
  amount: number | undefined;
  onUnitChange: (u: FoodUnit | undefined) => void;
  onAmountChange: (a: number) => void;
  className?: ClassValue;
  dataCy?: string;
}) => (
  <UnitAmountBase
    unit={props.foodUnit}
    units={props.units}
    amount={props.amount}
    onUnitChange={props.onUnitChange}
    onAmountChange={props.onAmountChange}
    className={props.className}
    dataCy={props.dataCy}
  />
);

/**
 * Wie `UnitAmount`, aber für `PurchaseUnit`s (z. B. wenn im Kochdialog ein
 * Produkt (Purchase) erfasst wurde).
 */
export const PurchaseUnitAmount = (props: {
  purchaseUnit: PurchaseUnit | undefined;
  purchaseUnits: PurchaseUnit[];
  amount: number | undefined;
  onUnitChange: (u: PurchaseUnit | undefined) => void;
  onAmountChange: (a: number) => void;
  className?: ClassValue;
  dataCy?: string;
}) => (
  <UnitAmountBase
    unit={props.purchaseUnit}
    units={props.purchaseUnits}
    amount={props.amount}
    onUnitChange={props.onUnitChange}
    onAmountChange={props.onAmountChange}
    className={props.className}
    dataCy={props.dataCy}
  />
);
